import React, { useState } from 'react';
import { Order } from '@hero/schema';
import { useTranslation } from 'react-i18next';
import { UserIcon, PhoneIcon, BuildingIcon, EyeIcon, PencilIcon } from '@hero/web-icons';
import { Collapse } from '@hero/web-components';
import { DetailRow } from './DetailRow';
import { useDriverDrawerStore } from '@hero/hooks-shared';
import { OrderDriverPickerDrawer } from '../OrderDriverPickerDrawer';

export interface DriverDetailsProps {
  order: Order;
}

export const DriverDetails: React.FC<DriverDetailsProps> = ({ order }) => {
  const { t } = useTranslation('m-order');
  const openDriverDrawer = useDriverDrawerStore(s => s.open);
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const driver = order.driver;
  const hasDriver = !!driver;

  const driverDescription = hasDriver
    ? `${driver.name}${driver.phone ? ` • ${driver.phone}` : ''}`
    : t('driver_details.no_driver_assigned');

  return (
    <div className="p-5 bg-white border border-gray-200 rounded-lg shadow-sm">
      <Collapse
        title={t('driver_details.title')}
        description={driverDescription}
        icon={
          <div className="p-1.5 bg-green-50 rounded-lg">
            <UserIcon className="size-5 text-green-600" />
          </div>
        }
        action={
          <div className="flex items-center gap-1.5">
            {hasDriver && driver?._id && (
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  openDriverDrawer(String(driver._id));
                }}
                className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                aria-label={t('driver_details.view_driver')}>
                <EyeIcon className="w-3.5 h-3.5 text-gray-600" />
              </button>
            )}
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                setIsPickerOpen(true);
              }}
              className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
              aria-label={
                hasDriver
                  ? t('drawer.driver_picker.title_reassign')
                  : t('drawer.driver_picker.title')
              }>
              <PencilIcon className="w-3.5 h-3.5 text-gray-600" />
            </button>
          </div>
        }
        defaultOpen={false}
        disabled={!hasDriver}>
        {hasDriver && (
          <div className="space-y-0">
            <DetailRow
              icon={<UserIcon className="size-5 text-gray-900" />}
              label={t('driver_details.driver_name', 'Driver Name')}
              value={driver.name}
            />

            {driver.phone && (
              <DetailRow
                icon={<PhoneIcon className="size-5 text-gray-900" />}
                label={t('driver_details.driver_phone', 'Driver Phone')}
                value={driver.phone}
              />
            )}

            {order.carrier?.name && (
              <DetailRow
                icon={<BuildingIcon className="size-5 text-gray-900" />}
                label={t('driver_details.company')}
                value={order.carrier.name}
                showDivider={false}
              />
            )}
          </div>
        )}
      </Collapse>

      <OrderDriverPickerDrawer
        order={order}
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
      />
    </div>
  );
};
